import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";

// Only expose the safe manual transitions here — table ownership flips must
// go through the POS/settlement pathways.
const STATUS_OPTIONS = ["available", "reserved", "blocked"];

export default function TablesPage() {
  const { branchId, hasPerm } = useAuth();
  const [tables, setTables] = useState([]);
  const [orders, setOrders] = useState({}); // { order_id: order }
  const [reservations, setReservations] = useState({}); // { reservation_id: res }
  const [users, setUsers] = useState({}); // { user_id: user }
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!branchId) return;
    try {
      const { data } = await api.get("/tables", { params: { branch_id: branchId } });
      setTables(data);
      // Fetch owning orders so we can render Primary/Companion badges and
      // full ownership details (order number, guest, server).
      const orderIds = Array.from(new Set(
        data.map((t) => t.current_order_id).filter(Boolean)
      ));
      if (orderIds.length === 0) {
        setOrders({}); setReservations({}); setUsers({});
      } else {
        const results = await Promise.all(orderIds.map((oid) =>
          api.get(`/orders/${oid}`).then((r) => r.data).catch(() => null)
        ));
        const byId = {};
        results.forEach((o) => { if (o) byId[o.id] = o; });
        setOrders(byId);
        // Reservations (customer name).
        const resIds = Array.from(new Set(
          results.filter(Boolean).map((o) => o.reservation_id).filter(Boolean)
        ));
        if (resIds.length) {
          const resList = await Promise.all(resIds.map((rid) =>
            api.get(`/reservations/${rid}`).then((r) => r.data).catch(() => null)
          ));
          const rBy = {};
          resList.forEach((r) => { if (r) rBy[r.id] = r; });
          setReservations(rBy);
        } else { setReservations({}); }
        // Server users — fetch once per branch (list endpoint is cheaper
        // than N per-user calls; there's no /users/{id} route).
        const userIds = new Set(
          results.filter(Boolean).map((o) => o.server_user_id).filter(Boolean)
        );
        if (userIds.size) {
          try {
            const { data: uList } = await api.get("/users",
              { params: { branch_id: branchId } });
            const uBy = {};
            (uList || []).forEach((u) => { if (userIds.has(u.id)) uBy[u.id] = u; });
            setUsers(uBy);
          } catch (_) { setUsers({}); }
        } else { setUsers({}); }
      }
    } catch (_) { setTables([]); setOrders({}); setReservations({}); setUsers({}); }
    finally { setLoading(false); }
  };
  useEffect(() => { setLoading(true); load(); const iv = setInterval(load, 10000); return () => clearInterval(iv); }, [branchId]);

  const setStatus = async (t, status) => {
    try {
      await api.post(`/tables/${t.id}/status`, { status });
      setTables((ts) => ts.map((x) => x.id === t.id ? { ...x, status } : x));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const markClean = async (t) => {
    try {
      await api.post(`/tables/${t.id}/mark-clean`, { notes: "" });
      toast.success(`${t.name} marked clean`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const byZone = tables.reduce((acc, t) => { (acc[t.zone] ||= []).push(t); return acc; }, {});

  const badgeFor = (t) => {
    const owning = t.current_order_id ? orders[t.current_order_id] : null;
    if (!owning) return null;
    if (owning.table_id === t.id) {
      return { label: "PRIMARY", cls: "text-violet-300 border-violet-500/40 bg-violet-500/10" };
    }
    if ((owning.linked_table_ids || []).includes(t.id)) {
      return { label: "COMPANION", cls: "text-sky-300 border-sky-500/40 bg-sky-500/10" };
    }
    return null;
  };

  const ownershipFor = (t) => {
    const owning = t.current_order_id ? orders[t.current_order_id] : null;
    if (!owning) return null;
    const res = owning.reservation_id ? reservations[owning.reservation_id] : null;
    const server = owning.server_user_id ? users[owning.server_user_id] : null;
    return {
      order_number: owning.order_number || `#${(owning.id || "").slice(-6).toUpperCase()}`,
      customer_name: res?.customer_name || "Walk-in",
      guest_count: owning.guest_count || res?.actual_head_count || res?.guest_count || null,
      server_name: server?.name || server?.email || "—",
      op_status: owning.table_operation_status || null,
    };
  };

  return (
    <div className="p-6 lg:p-8" data-testid="tables-page">
      <div className="mb-8">
        <div className="tiny-label text-violet-400">Floor plan</div>
        <h1 className="mt-1 text-4xl font-extrabold tracking-tight">Tables &amp; zones</h1>
        <p className="mt-1 text-zinc-500">Live floor state. Occupied tables are driven from the POS tab.</p>
      </div>

      {!branchId && <div className="panel p-8 text-center text-zinc-400">Select a branch first.</div>}
      {branchId && Object.keys(byZone).length === 0 && !loading && (
        <div className="panel p-8 text-center text-zinc-500">No tables configured yet.</div>
      )}

      <div className="space-y-8">
        {Object.entries(byZone).map(([zone, ts]) => (
          <div key={zone}>
            <div className="tiny-label mb-3">{zone.replace(/_/g, " ")}</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
              {ts.map((t) => {
                const b = badgeFor(t);
                const own = ownershipFor(t);
                const showSelector = hasPerm("table.status_update")
                  && t.status !== "occupied"
                  && t.status !== "cleaning";
                return (
                <div key={t.id} className="panel p-4 flex flex-col gap-2" data-testid={`table-${t.name}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-white">{t.name}</span>
                    {t.vip && <span className="tiny-label text-amber-300">VIP</span>}
                  </div>
                  <div className="text-xs text-zinc-500">cap {t.capacity}</div>
                  <StatusBadge status={t.status || "available"} />
                  {b && (
                    <span className={`inline-flex w-fit items-center px-2 h-5 rounded-full text-[9px] uppercase tracking-widest border font-bold ${b.cls}`}
                          data-testid={`table-role-${t.name}`}>
                      {b.label}
                    </span>
                  )}
                  {own && (
                    <div className="mt-1 space-y-0.5 text-[11px] leading-snug"
                          data-testid={`table-ownership-${t.name}`}>
                      <div className="font-bold text-white mono"
                            data-testid={`table-order-${t.name}`}>
                        {own.order_number}
                      </div>
                      <div className="text-zinc-300 truncate"
                            data-testid={`table-customer-${t.name}`}>
                        {own.customer_name}
                      </div>
                      {own.guest_count && (
                        <div className="text-zinc-400"
                              data-testid={`table-guests-${t.name}`}>
                          {own.guest_count} guest{own.guest_count === 1 ? "" : "s"}
                        </div>
                      )}
                      <div className="text-zinc-400 truncate"
                            data-testid={`table-server-${t.name}`}>
                        Server: {own.server_name}
                      </div>
                    </div>
                  )}
                  {own?.op_status === "in_progress" && (
                    <span className="text-[9px] uppercase tracking-widest text-amber-300 font-bold"
                          data-testid={`table-op-inprogress-${t.name}`}>
                      op in progress
                    </span>
                  )}
                  {own?.op_status === "requires_repair" && (
                    <span className="text-[9px] uppercase tracking-widest text-red-300 font-bold"
                          data-testid={`table-op-repair-${t.name}`}>
                      repair needed
                    </span>
                  )}
                  {t.status === "cleaning" && hasPerm("table.status_update") && (
                    <button onClick={() => markClean(t)}
                             data-testid={`table-mark-clean-${t.name}`}
                             className="mt-1 h-9 rounded-md bg-emerald-600/90 hover:bg-emerald-500 text-white text-xs font-bold uppercase tracking-wider">
                      Mark clean
                    </button>
                  )}
                  {showSelector && (
                    <select
                      value={t.status || "available"}
                      onChange={(e) => setStatus(t, e.target.value)}
                      data-testid={`table-status-${t.name}`}
                      className="mt-1 h-9 rounded-md bg-zinc-950 border border-white/10 text-xs text-white px-2 focus:ring-1 focus:ring-violet-500"
                    >
                      {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  )}
                  {t.status === "occupied" && !showSelector && (
                    <div className="text-[9px] uppercase tracking-widest text-zinc-500"
                          data-testid={`table-locked-${t.name}`}>
                      Managed by POS
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
